import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { translate } from "utils";
import { Button, Spin, Input, Tag } from "antd";
import { SaveOutlined, LeftOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import CheckAccess from "hoc/CheckAccess";
import { PERMISSION } from "constants/permission";
import Files from "./Files";
import Loading from "components/Loading";
import moment from "moment";
import {
	selectSubscriptionTransactionDetail,
	getSubscriptionTransactionDetailAsync,
	updateSubscriptionTransactionAsync,
} from "features/subscription-transaction/redux/subscription-transaction.slice";
import {
	SUBSCRIPTION_TRANSACTION_STATUS,
	DataUpdate,
} from "features/subscription-transaction/type";
import {
	paymentMethods,
	subscriptionTransactionStatus,
} from "features/subscription-transaction/constants";
import { selectLang } from "store/lang/langSlice";
import { USER_TYPE } from "types";
import { APP_PATH } from "constants/path";
import { Link } from "react-router-dom";
import ProductList from "./ProductList";

export default function SubscriptionBillingInforDetail() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { billId = "", id = "" } = useParams();
	const [loading, setLoading] = useState(false);
	const subscriptionTransactionDetail = useSelector(
		selectSubscriptionTransactionDetail,
	);
	const lang = useSelector(selectLang);

	const [dataUpdate, setDataUpdate] = useState<DataUpdate>({
		note: "",
		files: [],
		status: SUBSCRIPTION_TRANSACTION_STATUS.new,
	});
	const [isErrorFile, setIsErrorFile] = useState<boolean>(false);

	useEffect(() => {
		setLoading(true);
		dispatch(
			getSubscriptionTransactionDetailAsync({
				id: billId || id,
				callback: () => {
					setLoading(false);
				},
			}),
		);
	}, []);

	useEffect(() => {
		if (!subscriptionTransactionDetail) return;
		const { files, note, status } = subscriptionTransactionDetail;
		setDataUpdate({
			files: (files || []).map(el => {
				return {
					...el,
					path: el.url || "",
				};
			}),
			note,
			status,
		});
		setIsErrorFile(false);
	}, [subscriptionTransactionDetail]);

	const handleUpdate = async () => {
		if (!dataUpdate.files?.length) {
			setIsErrorFile(true);
			return;
		}
		setLoading(true);
		dispatch(
			updateSubscriptionTransactionAsync({
				id: billId || id,
				data: {
					...dataUpdate,
					files: dataUpdate.files.map(el => {
						return {
							...el,
							updatedBy: typeof el.updatedBy === "object" ? el.updatedBy.id : 0,
						};
					}),
				},
				callback: () => {
					setLoading(false);
				},
			}),
		);
	};

	if (
		!subscriptionTransactionDetail ||
		!subscriptionTransactionDetail.merchantSubscription
	) {
		return <Loading scope="local" />;
	}

	const {
		status,
		extraData,
		quotation,
		createdAt,
		merchantSubscription,
		method,
		createdBy,
		createdByType,
		merchant,
	} = subscriptionTransactionDetail;

	const {
		package: { name },
	} = extraData;

	const { startDate } = merchantSubscription;

	const statusFound = subscriptionTransactionStatus.find(
		el => el.value === status,
	);

	const paymentMethodFound = paymentMethods.find(el => el.value === method);

	return (
		<Spin spinning={loading}>
			<div className="fade-in h-full my-5">
				<div className="bg-white p-4 shadow-lg">
					<div>
						<p className="flex justify-between items-center font-semibold text-lg">
							{translate("Main information")}
							<Tag color={statusFound?.color || ""}>
								{translate(statusFound?.name || "")}
							</Tag>
						</p>
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-x-5 gap-y-3 mt-2">
							<div>
								<label htmlFor="packageName">
									{translate("Subscription package")}
								</label>
								<Input
									placeholder={translate("Subscription package")}
									id="packageName"
									disabled
									value={name[lang]}
								/>
							</div>
							<div>
								<label htmlFor="merchantName">{translate("Merchant")}</label>
								<Input
									placeholder={translate("Merchant")}
									id="merchantName"
									disabled
									value={merchant?.fullName}
								/>
							</div>
							<div>
								<label htmlFor="">{translate("Valid from")}</label>
								<Input
									placeholder={translate("Valid from")}
									id=""
									disabled
									value={moment(startDate).format("DD/MM/YYYY")}
								/>
							</div>
							<div>
								<label htmlFor="">{translate("Valid to")}</label>
								<Input
									placeholder={translate("Valid to")}
									id=""
									disabled
									value={moment(startDate)
										.add(quotation, "M")
										.format("DD/MM/YYYY")}
								/>
							</div>
							<div>
								<label htmlFor="">{translate("Created date")}</label>
								<Input
									placeholder={translate("Created date")}
									id=""
									disabled
									value={moment(createdAt).format("DD/MM/YYYY HH:mm")}
								/>
							</div>
							<div>
								<label htmlFor="">{translate("Created by")}</label>
								<div className="relative">
									<Input id="" disabled />
									{!!createdBy && (
										<Link
											to={
												(createdByType === USER_TYPE.MERCHANT
													? APP_PATH.merchants
													: APP_PATH.staffs) +
												"/" +
												createdBy.id
											}
											className="w-full h-full absolute top-0 left-0 pl-3 text-blue-500 flex items-center"
											target="_blank"
										>
											{createdBy.fullName}
										</Link>
									)}
								</div>
							</div>
							<div>
								<label htmlFor="">{translate("Payment type")}</label>
								<Input
									placeholder={translate("Payment type")}
									id=""
									disabled
									value={translate(paymentMethodFound?.name || "")}
								/>
							</div>
							<div>
								<label htmlFor="note">{translate("Note")}</label>
								<Input.TextArea
									rows={3}
									placeholder={translate("Note")}
									id="note"
									maxLength={255}
									value={dataUpdate.note || ""}
									onChange={e => {
										setDataUpdate({
											...dataUpdate,
											note: e.target.value,
										});
									}}
								/>
							</div>
						</div>
					</div>
				</div>

				{/* Product list */}
				<ProductList subScriptionTransaction={subscriptionTransactionDetail} />

				{/* Files */}
				<Files
					files={dataUpdate.files || []}
					filesChange={files => {
						setDataUpdate({
							...dataUpdate,
							files: files,
						});
					}}
					isShowError={isErrorFile}
				/>

				{/* Action btns */}
				<div className="mt-6 flex justify-center sm:justify-end items-center gap-2">
					<Button
						icon={<LeftOutlined />}
						type="primary"
						className="flex items-center bg-[#595959] focus:bg-[#8c8c8c] active:bg-[#8c8c8c] hover:bg-[#8c8c8c] border-0"
						onClick={() => {
							navigate(-1);
						}}
					>
						{translate("Go back")}
					</Button>
					<CheckAccess perms={[PERMISSION.BILLING_INFORMATION.UPDATE]}>
						<Button
							htmlType="submit"
							icon={<SaveOutlined />}
							type="primary"
							className="flex items-center bg-[#1890ff] mr-2"
							onClick={handleUpdate}
						>
							{translate("Save")}
						</Button>
					</CheckAccess>
				</div>
			</div>
		</Spin>
	);
}
